<template>
    <div class="categories">
        <template v-if="categories?.length">
            <CategoryPill v-for="categoryUid in categories" :key="categoryUid" :category="categoryUid" :label="getCategoryLabel(categoryUid)" />
        </template>
        <template v-else>
            <CategoryPill v-if="category" :category="category" />
        </template>
    </div>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import type {
        ArticleDocument,
        ArticleDocumentData,
        EventDocument,
        EventDocumentData,
        GuideDocument,
        GuideDocumentData,
        ResourceCategoryDocument,
        SuccessStoryDocument,
        SuccessStoryDocumentData,
    } from '@prismicio-types';
    import {usePrismicTypeGuards} from '#imports';
    import CategoryPill from '~/components/CategoryPill.vue';

    const props = defineProps<{
        document:
            | ArticleDocument
            | FilledContentRelationshipField<'article', string, ArticleDocumentData>
            | EventDocument
            | FilledContentRelationshipField<'event', string, EventDocumentData>
            | GuideDocument
            | FilledContentRelationshipField<'guide', string, GuideDocumentData>
            | SuccessStoryDocument
            | FilledContentRelationshipField<'successStory', string, SuccessStoryDocumentData>;
    }>();

    interface ResourceCategory {
        resourceCategory: ResourceCategoryDocument;
    }

    const {hasProperty} = usePrismicTypeGuards();

    const optionalResourceCategories = computed((): ResourceCategory[] | undefined => {
        const data = props.document.data;
        if (!data || !hasProperty(data, 'resourceCategories')) {
            return undefined;
        }
        return data.resourceCategories as ResourceCategory[];
    });

    const category = computed(() => {
        const data = props.document.data;
        if (!data || !hasProperty(data, 'category')) {
            return undefined;
        }
        return data.category;
    });

    const categories = computed(() => {
        const resourceCategories = optionalResourceCategories.value
            ?.map((category) => category.resourceCategory.uid)
            .filter((uid) => uid !== undefined);
        if (resourceCategories && resourceCategories.length > 0) {
            return resourceCategories;
        }
        return category.value ? [category.value] : undefined;
    });

    const getCategoryLabel = (categoryUid: string) => {
        const resourceCategory = optionalResourceCategories.value?.find((cat) => cat.resourceCategory.uid === categoryUid)?.resourceCategory;

        if (resourceCategory?.data?.name) {
            return resourceCategory.data.name;
        }
        return undefined;
    };
</script>

<style lang="scss" scoped>
    .categories {
        display: flex;
        gap: var(--joy-core-spacing-2);
        flex-wrap: wrap;
        flex: 1;
    }
</style>
